import {Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormBuilder,} from '@angular/forms';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {BehaviorSubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ActionState} from 'src/app/enums/action-state.enum';
import {BadgeRepo} from 'src/app/repositories/badge-repo';
import {SubscriptionRepo} from 'src/app/repositories/subscription-repo';
import {AuthSupportPagedList, AuthSupportType, SubscriptionSummary,} from 'src/swagger-gen/web-secured/models';
import {AssigningBadgeType} from '../../models/badge/assigning-badge';
import {noWhitespaceValidator} from '../../validators/custom.validators';

@Component({
    selector: 'app-modal-badge-assignment',
    templateUrl: './modal-badge-assignment.component.html',
    styleUrls: ['./modal-badge-assignment.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalBadgeAssignmentComponent implements OnInit, OnDestroy {
    destroyed$ = new Subject<void>();
    loadingState = new BehaviorSubject('');
    actionState = ActionState;
    subList!: SubscriptionSummary[];
    error: any;

    selectedSubUid!: string;
    logger: any;
    badgesList!: AuthSupportPagedList;

    assigningBadgeType = AssigningBadgeType;
    badgeTypes = Object.values(AuthSupportType);

    assigningBadgeForm = this.fb.group({
        selectionMode: [AssigningBadgeType.EXISTING],
        existingBadgeUid: [''],
        newBadge: this.fb.group({
            badgeType: [''],
            badgeValue: [''],
        }),
        selectedBadgeName: [''],
    });
    submitted: boolean;
    existingBadges: boolean;
    success: boolean;
    selectedSubName: string;
    selectedBadgeNumber: string;

    get selectionModeCtrl() {
        return this.assigningBadgeForm.get('selectionMode');
    }

    get existingBadgeUidCtrl() {
        return this.assigningBadgeForm.get('existingBadgeUid');
    }

    get newBadgeCtrl() {
        return this.assigningBadgeForm.get('newBadge');
    }

    get typeCtrl() {
        return this.newBadgeCtrl.get('badgeType');
    }

    get valueCtrl() {
        return this.newBadgeCtrl.get('badgeValue');
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private subscriptionRepo: SubscriptionRepo,
        private badgeRepo: BadgeRepo,
        private fb: UntypedFormBuilder
    ) {
    }

    ngOnInit(): void {
        this.subList = this.data.userObj.subscriptionInfos;
        this.selectedSubUid = this.subList[0].uid;
        this.selectedSubName = this.subList[0].name;

        this.getExistingBadges();

        this.selectionModeCtrl.valueChanges.subscribe((value) => {
            if (value === AssigningBadgeType.NEW) {
                this.setFormValidators(false);
                this.typeCtrl.patchValue(this.badgeTypes[0]);
            } else {
                this.setFormValidators(true);
            }
        });

        // this.typeCtrl.valueChanges.subscribe((value) => console.log(value));
    }

    onChangeSubscription(uid: string, name: string) {
        this.selectedSubUid = uid;
        this.selectedSubName = name;
    }

    setFormValidators(existingBadge: boolean) {
        if (!existingBadge) {
            this.valueCtrl.setValidators(noWhitespaceValidator);
            this.valueCtrl.updateValueAndValidity();
        } else {
            this.valueCtrl.clearValidators();
            this.valueCtrl.updateValueAndValidity();
        }
    }

    getParamsExistingBadge() {
        const params = [];
        params.push({
            key: 'sortFieldName',
            value: 'VALUE',
        });
        params.push({key: 'sortDirection', value: 'ASC'});
        params.push({
            key: 'availableBadgeOnly',
            value: 'true',
        });
        return params;
    }

    getExistingBadges() {
        this.loadingState.next(ActionState.LOADING);
        const params = this.getParamsExistingBadge();
        this.badgeRepo
            .getBadgeList(null, null, params)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (badgesList: AuthSupportPagedList) => {
                    this.badgesList = badgesList;

                    if (this.badgesList.list?.length > 0) {
                        this.existingBadges = true;
                        this.existingBadgeUidCtrl.patchValue(this.badgesList.list[0].uid);
                    }
                    this.loadingState.next(ActionState.READY);
                },
                error: (error) => {
                    this.error = error.error;
                    this.loadingState.next(ActionState.FAILED);
                },
            });
    }

    onSubmit() {
        if (!this.existingBadges && this.selectionModeCtrl.value === AssigningBadgeType.EXISTING) {
            return;
        }
        this.submitted = true;
        this.assigningBadgeForm.markAllAsTouched();
        if (this.assigningBadgeForm.valid) {
            this.loadingState.next(ActionState.LOADING);
            const formValue = this.assigningBadgeForm.value;
            const body: any = {};
            if (formValue.selectionMode === AssigningBadgeType.EXISTING) {
                body.uid = formValue.existingBadgeUid;
                this.badgesList.list.forEach((badge) => {
                    if (badge.uid === body.uid) {
                        this.selectedBadgeNumber = badge.value;
                    }
                });
            } else if (formValue.selectionMode === AssigningBadgeType.NEW) {
                body.type = formValue.newBadge.badgeType;
                body.value = formValue.newBadge.badgeValue;
                this.selectedBadgeNumber = body.value;
            }

            this.subscriptionRepo
                .assignBadgeToSubscription(this.selectedSubUid, body)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (res) => {
                        this.loadingState.next(ActionState.SUCCESS);
                    },
                    error: (error) => {
                        this.error = error.error;
                        this.loadingState.next(ActionState.FAILED);
                    },
                });
        } else {
            return;
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
